/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import {
  Fiche /* , useFicheServiceStatus */,
  FicheContainerContext,
} from '@mairistem/fiche';

import type { Individu } from '@/types';
// import { recenseFicheService } from '@/services';

import { RecenseListe } from '../Liste';
import RecensementPanel from './Panels/Recensement/RecensementPanel';
import IdentificationPanel from './Panels/Identification/IdentificationPanel';
import FiliationPanel from './Panels/Filiation/FiliationPanel';
import AdressesContactsPanel from './Panels/AdressesContacts/AdressesContactsPanel';
import InformationsComplementairesPanel from './Panels/InformationsComplementaires/InformationsComplementairesPanel';

interface RecenseFicheProps {
  loading?: boolean;
  errors?: { id: string; message: string }[];
  recense?: Individu;
  onChange?: (data: Individu, shouldUpdateStatus: boolean) => void;
}

const RecenseFiche = (props: RecenseFicheProps) => {
  const { loading, errors, recense: recenseProps, onChange } = props;

  const { service } = React.useContext(FicheContainerContext);
  const { update } = service || {};
  const [recense, setRecense] = React.useState<Individu>(recenseProps);

  const handleChange = React.useCallback((data) => {
    setRecense((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  const handleSave = React.useCallback(async () => {
    const body = {
      ...recense,
      identifiantDiplome: recense?.diplome?.identifiant || null,
      identifiantSituationScolaire:
        recense?.situationScolaire?.identifiant || null,
      identifiantSpecialite: recense?.specialite?.identifiant || null,
      identifiantProfession: recense?.profession?.identifiant || null,
    };

    return update(body)
      .then((data) => {
        onChange(data, false);
        setRecense(data);
        toast('Enregistrement effectué avec succès', { type: 'success' });
      })
      .catch(() => {
        toast('Une erreur est survenue lors de la sauvegarde', {
          type: 'error',
        });
      });
  }, [onChange, recense, update]);

  const handleRollback = React.useCallback(() => {
    setRecense(recenseProps);
  }, [recenseProps]);

  const updated = React.useMemo(
    () => !_.isEqual(recense, recenseProps),
    [recense, recenseProps],
  );

  const recensementPanel = React.useMemo(
    () => (
      <Fiche.Panel key="recensement" id="recensement" title="RECENSEMENT" open>
        <RecensementPanel
          demandeExemption={recense?.demandeExemption}
          recensement={recense?.recensement}
          numeroRecensement={recense?.numeroRecensement}
          trimestreRecensement={recense?.trimestreRecensement}
          anneeRecensement={recense?.anneeRecensement}
          dateRecensement={recense?.dateRecensement}
          recense={recenseProps}
          updated={updated}
          onChange={handleChange}
          onRollback={handleRollback}
          onSave={handleSave}
        />
      </Fiche.Panel>
    ),
    [
      recense?.dateRecensement,
      recense?.demandeExemption,
      recense?.numeroRecensement,
      recense?.recensement,
      recense?.trimestreRecensement,
      recense?.anneeRecensement,
      recenseProps,
      updated,
      handleChange,
      handleSave,
      handleRollback,
    ],
  );

  const identificationPanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="identification"
        id="identification"
        title="IDENTIFICATION"
        open
      >
        <IdentificationPanel
          identifiant={recense?.identifiant}
          dateRecensement={recense?.dateRecensement}
          minuteNaissance={recense?.minuteNaissance}
          heureNaissance={recense?.heureNaissance}
          jourNaissance={recense?.jourNaissance}
          moisNaissance={recense?.moisNaissance}
          anneeNaissance={recense?.anneeNaissance}
          typeDateNaissance={recense?.typeDateNaissance}
          sexe={recense?.sexe}
          nomFamille={recense?.nomFamille}
          nomUsage={recense?.nomUsage}
          prenom={recense?.prenom}
          prenomsComplementaires={recense?.prenomsComplementaires}
          adresseNaissance={recense?.adresseNaissance}
          situationFamiliale={recense?.situationFamiliale}
          pupille={recense?.pupille}
          nombreFreresSoeurs={recense?.nombreFreresSoeurs}
          nombreEnfants={recense?.nombreEnfants}
          recense={recenseProps}
          updated={updated}
          onChange={handleChange}
          onRollback={handleRollback}
          onSave={handleSave}
        />
      </Fiche.Panel>
    ),
    [
      recense?.adresseNaissance,
      recense?.anneeNaissance,
      recense?.dateRecensement,
      recense?.heureNaissance,
      recense?.jourNaissance,
      recense?.minuteNaissance,
      recense?.moisNaissance,
      recense?.typeDateNaissance,
      recense?.nomFamille,
      recense?.nomUsage,
      recense?.prenom,
      recense?.prenomsComplementaires,
      recense?.sexe,
      recense?.situationFamiliale,
      recense?.pupille,
      recense?.nombreFreresSoeurs,
      recense?.nombreEnfants,
      recense?.identifiant,
      recenseProps,
      updated,
      handleChange,
      handleRollback,
      handleSave,
    ],
  );

  const adressesPanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="adresses"
        id="adresses"
        title="ADRESSES ET CONTACTS"
        open
      >
        <AdressesContactsPanel
          tel1={recense?.tel1}
          tel2={recense?.tel2}
          mail={recense?.mail}
          adresseRattachement={recense?.adresseRattachement}
          adresseContact={recense?.adresseContact}
          isResideCommune={recense?.isResideCommune}
          recensement={recense?.recensement}
          recense={recenseProps}
          updated={updated}
          onChange={handleChange}
          onRollback={handleRollback}
          onSave={handleSave}
        />
      </Fiche.Panel>
    ),
    [
      recense?.tel1,
      recense?.tel2,
      recense?.mail,
      recense?.adresseRattachement,
      recense?.adresseContact,
      recense?.isResideCommune,
      recense?.recensement,
      recenseProps,
      updated,
      handleChange,
      handleRollback,
      handleSave,
    ],
  );

  const filiationPanel = React.useMemo(
    () => (
      <Fiche.Panel key="filiation" id="filiation" title="FILIATION" open>
        <FiliationPanel
          identifiant={recense?.identifiant}
          parent1={recense?.parent1}
          parent2={recense?.parent2}
          dateNaissanceRecense={{
            anneeNaissance: recense?.anneeNaissance,
            moisNaissance: recense?.moisNaissance,
            jourNaissance: recense?.jourNaissance,
          }}
          recense={{
            ...recenseProps,
            filiationInconnueParent1: recense?.filiationInconnueParent1,
            filiationInconnueParent2: recense?.filiationInconnueParent2,
          }}
          updated={updated}
          onChange={handleChange}
          onRollback={handleRollback}
          onSave={handleSave}
        />
      </Fiche.Panel>
    ),
    [
      handleChange,
      handleRollback,
      handleSave,
      recense?.parent1,
      recense?.filiationInconnueParent1,
      recense?.filiationInconnueParent2,
      recense?.parent2,
      recense?.identifiant,
      recense?.anneeNaissance,
      recense?.moisNaissance,
      recense?.jourNaissance,
      recenseProps,
      updated,
    ],
  );

  const informationsComplementairesPanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="informationsComplementaires"
        id="informationsComplementaires"
        title="INFORMATIONS COMPLÉMENTAIRES"
        open
      >
        <InformationsComplementairesPanel
          situationScolaire={recense?.situationScolaire}
          diplome={recense?.diplome}
          specialite={recense?.specialite}
          profession={recense?.profession}
          sexe={recense?.sexe}
          identifiantNationaliteBis={recense?.identifiantNationaliteBis}
          scolarise={recense?.scolarise}
          activiteProfessionnelle={recense?.activiteProfessionnelle}
          recense={recenseProps}
          updated={updated}
          onChange={handleChange}
          onRollback={handleRollback}
          onSave={handleSave}
        />
      </Fiche.Panel>
    ),
    [
      handleChange,
      handleRollback,
      handleSave,
      updated,
      recenseProps,
      recense?.situationScolaire,
      recense?.diplome,
      recense?.sexe,
      recense?.specialite,
      recense?.profession,
      recense?.identifiantNationaliteBis,
      recense?.scolarise,
      recense?.activiteProfessionnelle,
    ],
  );

  React.useEffect(() => {
    if (!_.isNil(recenseProps)) {
      setRecense(recenseProps);
    }
  }, [recenseProps]);

  const header = React.useMemo(
    () => `${_.toUpper(recense?.nomFamille) || ''} ${recense?.prenom || ''}`,
    [recense?.nomFamille, recense?.prenom],
  );

  const list = React.useMemo(
    () => ({
      left: <RecenseListe />,
    }),
    [],
  );

  return (
    <Fiche
      key="RecenseFiche"
      type="recense"
      header={header}
      loading={loading}
      errors={errors}
      sidebars={list}
    >
      {recensementPanel}
      {identificationPanel}
      {adressesPanel}
      {filiationPanel}
      {informationsComplementairesPanel}
    </Fiche>
  );
};

export default RecenseFiche;
